/* Factory used by the 3 "sacred teachers" pages (Ahura Mazda, Yahvé, Sanat Kumara).
   Keeps the DOM/CSS consistent and reduces 3× 220-line clones to a single component.
*/
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Send, Loader2, Trash2, Sparkles, BookOpen, Headphones, X } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/lib/lang";
import { useAccount } from "@/lib/account";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";
import { ChatDisclaimer } from "@/components/AiNotice";
import { speak, stopSpeaking, isSpeechSupported } from "@/lib/voice";

/**
 * props:
 *   persona: "ahura" | "yahve" | "sanat"
 *   title: { fr, en }
 *   subtitle: { fr, en }
 *   greeting: { fr, en }
 *   suggestions: { fr: string[], en: string[] }
 *   playlist: MusicPicker playlist
 *   accent: hex color
 *   theme: { bg, fg, msgBg, chipBg, bgRadial }  // CSS color tokens
 *   IconComponent: lucide icon
 *   personaLabel: string shown in Favorites mini-player
 */
export default function SacredChatPage({
  persona, title, subtitle, greeting, suggestions,
  playlist, accent, theme, IconComponent, personaLabel,
  lessonMode, // optional: { endpoint, topics: { fr: [...], en: [...] }, title: {fr, en} }
  unlimited = false, // when true: no quota counter, labeled "free unlimited"
  replySound, // optional URL — played once at every new advisor reply (thunder for Zeus, etc.)
  sfxEmoji = "⚡", // emoji used in the SFX toggle button
  sfxLabel, // optional { fr, en } label used in the toggle tooltip
  kickerLabel, // optional { fr, en } override of the kicker tagline
  backTo = "/",
  inputPlaceholder, // optional { fr, en }
}) {
  const { lang, setLang } = useLang();
  const { email, status } = useAccount() || {};
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const SESSION_KEY = `lechatceltique.${persona}_session`;
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);
  // Reply sound (persisted ON/OFF in localStorage so Zeus doesn't re-nag on reload)
  const SFX_KEY = `lechatceltique.${persona}_sfx_on`;
  const [sfxOn, setSfxOn] = useState(() => localStorage.getItem(SFX_KEY) !== "false");
  const sfxAudioRef = useRef(null);
  // Lesson mode state
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lessonTopic, setLessonTopic] = useState("");
  const [lessonLoading, setLessonLoading] = useState(false);
  const [lessonPlayingIdx, setLessonPlayingIdx] = useState(null);
  const voiceSupported = isSpeechSupported();

  const playReplySound = () => {
    if (!replySound || !sfxOn) return;
    const el = sfxAudioRef.current;
    if (!el) return;
    try {
      el.volume = 0.55;
      el.currentTime = 0;
      el.play().catch(() => {});
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!sessionId) return;
    client.get(`/chat/${persona}/history`, { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [sessionId, persona]);

  useEffect(() => {
    client.get(`/chat/${persona}/usage`, { params: email ? { email } : {} })
      .then((r) => setUsage(r.data))
      .catch(() => {});
  }, [email, status?.premium_active, messages.length, persona]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { sender: "user", message: trimmed, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await client.post(`/chat/${persona}`, {
        message: trimmed, session_id: sessionId || null, email: email || null, lang,
      });
      const data = r.data;
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: data.reply, created_at: new Date().toISOString() }]);
      playReplySound();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.message) {
        toast.error(detail.message);
        setUsage((u) => ({ ...u, used_today: detail.used_today ?? u.used_today, remaining: 0 }));
      } else {
        toast.error(lang === "fr" ? "Le silence sacré. Réessaie dans un instant." : "Sacred silence. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    try { await client.post(`/chat/${persona}/clear`, { session_id: sessionId || null }); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(lang === "fr" ? "Session effacée." : "Session wiped.");
  };

  const sendLesson = async (rawTopic) => {
    const topic = (rawTopic ?? lessonTopic).trim();
    if (!topic || !lessonMode?.endpoint) return;
    setLessonOpen(false);
    setLessonTopic("");
    const userLabel = lang === "fr" ? `📚 Leçon demandée : ${topic}` : `📚 Lesson requested: ${topic}`;
    setMessages((m) => [...m, { sender: "user", message: userLabel, created_at: new Date().toISOString(), kind: "lesson_request" }]);
    setLessonLoading(true);
    try {
      const r = await client.post(lessonMode.endpoint, {
        topic, session_id: sessionId || null, email: email || null, lang,
      });
      const data = r.data;
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: data.reply, created_at: new Date().toISOString(), kind: "lesson" }]);
      playReplySound();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error(lang === "fr"
          ? "Doucement, l'étudiant impatient. Attends une minute avant la prochaine leçon."
          : "Easy, impatient student. Wait a minute before the next lesson.");
      } else {
        toast.error(lang === "fr" ? "Le feu vacille. Réessaie." : "The fire flickers. Try again.");
      }
    } finally {
      setLessonLoading(false);
    }
  };

  const toggleLessonTTS = (idx, text) => {
    if (lessonPlayingIdx === idx) {
      stopSpeaking();
      setLessonPlayingIdx(null);
      return;
    }
    // Strip markdown for cleaner audio
    const clean = text
      .replace(/#{1,6}\s?/g, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/^[-•]\s?/gm, "");
    setLessonPlayingIdx(idx);
    speak(clean, { lang, onEnd: () => setLessonPlayingIdx(null) });
  };

  const sg = lang === "fr" ? suggestions.fr : suggestions.en;

  return (
    <div data-testid={`${persona}-page`} className="sacred-root min-h-screen relative"
         style={{ background: theme.bgRadial, color: theme.fg, overflowX: "hidden" }}>
      {replySound && (
        <audio ref={sfxAudioRef} src={replySound} preload="auto" />
      )}
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to={backTo} data-testid={`${persona}-back`} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: accent }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: `${accent}22`, border: `1px solid ${accent}50` }}>
              <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                      data-testid={`${persona}-lang-fr`}
                      style={{ background: lang === "fr" ? accent : "transparent", color: lang === "fr" ? "#0a0612" : theme.fg, fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                      data-testid={`${persona}-lang-en`}
                      style={{ background: lang === "en" ? accent : "transparent", color: lang === "en" ? "#0a0612" : theme.fg, fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <MusicPicker playlist={playlist} accent={accent} testId={`${persona}-music`} persona={personaLabel} />
            {replySound && (
              <button
                type="button"
                onClick={() => {
                  const next = !sfxOn;
                  setSfxOn(next);
                  localStorage.setItem(SFX_KEY, String(next));
                }}
                data-testid={`${persona}-sfx-toggle`}
                title={sfxOn
                  ? (sfxLabel?.[lang] ? (lang === "fr" ? `Couper ${sfxLabel.fr}` : `Mute ${sfxLabel.en}`) : (lang === "fr" ? "Couper le son signature" : "Mute signature sound"))
                  : (sfxLabel?.[lang] ? (lang === "fr" ? `Activer ${sfxLabel.fr}` : `Enable ${sfxLabel.en}`) : (lang === "fr" ? "Activer le son signature" : "Enable signature sound"))}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full transition-all"
                style={{
                  background: sfxOn ? `${accent}44` : "rgba(255,255,255,0.06)",
                  border: `1px solid ${sfxOn ? accent + "aa" : "rgba(255,255,255,0.16)"}`,
                  color: sfxOn ? accent : "rgba(255,255,255,0.6)",
                  fontSize: 14,
                }}
              >
                {sfxOn ? sfxEmoji : <span style={{ textDecoration: "line-through", opacity: 0.6 }}>{sfxEmoji}</span>}
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div data-testid={`${persona}-portrait`} aria-hidden="true"
             className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mb-6"
             style={{ background: `radial-gradient(circle at 30% 30%, ${accent}55, rgba(0,0,0,0.5) 70%)`, boxShadow: `0 0 0 2px ${accent}99, 0 0 40px -6px ${accent}` }}>
          <IconComponent size={34} style={{ color: accent }} />
        </div>
        <div className="overline mb-4" style={{ color: accent }}>
          <span className="kicker-line" />
          {personaLabel} · {kickerLabel ? kickerLabel[lang] : unlimited
            ? (lang === "fr" ? "enseignement · gratuit illimité" : "teaching · free unlimited")
            : (lang === "fr" ? "sagesse · gratuit illimité" : "wisdom · free unlimited")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: theme.fg }}>
          {lang === "fr" ? title.fr : title.en}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: theme.msgFg || theme.fg }}>
          {lang === "fr" ? subtitle.fr : subtitle.en}
        </p>

        <div data-testid={`${persona}-usage`}
             className="mt-6 inline-flex items-center gap-3 text-xs font-mono px-4 py-2 rounded-full"
             style={{ background: `${accent}18`, border: `1px solid ${accent}60`, color: accent }}>
          <IconComponent size={13} />
          {unlimited
            ? (lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free")
            : (usage.premium_active
                ? (lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free")
                : (<>{usage.used_today ?? 0} / {usage.limit ?? 20} {lang === "fr" ? "échanges aujourd'hui" : "exchanges today"}</>))}
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        {lessonMode && (
          <div className="mb-5" data-testid={`${persona}-lesson-bar`}>
            <button
              type="button"
              onClick={() => setLessonOpen((v) => !v)}
              data-testid={`${persona}-lesson-open`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl transition-all"
              style={{
                background: `linear-gradient(135deg, ${accent}26, ${accent}10)`,
                border: `1.5px solid ${accent}80`,
                color: accent,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                boxShadow: `0 0 20px ${accent}22`,
              }}
            >
              <BookOpen size={16} />
              {lang === "fr" ? "📚 Demander une leçon (illimité)" : "📚 Request a lesson (unlimited)"}
            </button>
            {lessonOpen && (
              <div
                data-testid={`${persona}-lesson-panel`}
                className="mt-4 p-5 rounded-2xl"
                style={{
                  background: "rgba(0,0,0,0.35)",
                  border: `1px solid ${accent}44`,
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>
                    {lang === "fr"
                      ? (lessonMode.title?.fr || "Choisis ton sujet d'étude")
                      : (lessonMode.title?.en || "Choose your topic")}
                  </div>
                  <button
                    type="button"
                    onClick={() => setLessonOpen(false)}
                    data-testid={`${persona}-lesson-close`}
                    style={{ color: `${accent}bb`, padding: 4 }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(lang === "fr" ? lessonMode.topics?.fr : lessonMode.topics?.en)?.map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => sendLesson(t)}
                      disabled={lessonLoading}
                      data-testid={`${persona}-lesson-topic-${i}`}
                      style={{
                        padding: "8px 14px", borderRadius: 999,
                        background: `${accent}1a`, border: `1px solid ${accent}55`,
                        color: theme.fg, fontSize: 12,
                        fontFamily: "'IBM Plex Mono', monospace",
                        cursor: lessonLoading ? "wait" : "pointer",
                        opacity: lessonLoading ? 0.5 : 1,
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <form
                  onSubmit={(e) => { e.preventDefault(); sendLesson(); }}
                  className="flex gap-2"
                >
                  <input
                    data-testid={`${persona}-lesson-input`}
                    value={lessonTopic}
                    onChange={(e) => setLessonTopic(e.target.value)}
                    placeholder={lang === "fr" ? "Ou tape un sujet (ex: Yasna 28)…" : "Or type a topic (e.g. Yasna 28)…"}
                    style={{
                      flex: 1, padding: "10px 14px", borderRadius: 10,
                      background: "rgba(0,0,0,0.4)", border: `1px solid ${accent}40`,
                      color: theme.fg, fontSize: 14, outline: "none",
                    }}
                    disabled={lessonLoading}
                  />
                  <button
                    type="submit"
                    disabled={lessonLoading || !lessonTopic.trim()}
                    data-testid={`${persona}-lesson-submit`}
                    style={{
                      padding: "10px 16px", borderRadius: 10,
                      background: `linear-gradient(135deg, ${accent}, ${accent}bb)`,
                      color: "#0a0612", fontWeight: 600, fontSize: 13,
                      fontFamily: "'IBM Plex Mono', monospace",
                      opacity: (lessonLoading || !lessonTopic.trim()) ? 0.4 : 1,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {lessonLoading ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />}
                    {lang === "fr" ? "Enseigner" : "Teach"}
                  </button>
                </form>
                <p className="mt-3 text-[11px] font-mono" style={{ color: `${accent}99` }}>
                  {lang === "fr"
                    ? "⚡ Les leçons sont illimitées et ne consomment pas ton quota quotidien."
                    : "⚡ Lessons are unlimited and don't count against your daily quota."}
                </p>
              </div>
            )}
          </div>
        )}
        <div className="sacred-chat" data-testid={`${persona}-chat-box`}
             style={{ background: theme.msgBg, border: `1px solid ${accent}35`, borderRadius: 22, padding: 20 }}>
          <div ref={boxRef} className="sacred-messages" style={{ minHeight: 380, maxHeight: "62vh", overflowY: "auto", padding: "12px 4px 20px", marginBottom: 16 }}>
            {messages.length === 0 && (
              <div style={{ textAlign: "center", padding: "32px 20px" }}>
                <IconComponent size={22} style={{ color: accent }} />
                <p className="mt-3 font-serif text-xl italic" style={{ color: theme.fg }}>
                  {lang === "fr" ? greeting.fr : greeting.en}
                </p>
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: `${accent}aa` }}>
                  {lang === "fr" ? "Ou choisis une invocation :" : "Or pick an invocation:"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {sg.map((s, i) => (
                    <button key={i} type="button"
                            data-testid={`${persona}-suggestion-${i}`}
                            onClick={() => send(s)}
                            style={{
                              padding: "8px 14px", borderRadius: 999,
                              background: `${accent}20`, border: `1px solid ${accent}55`,
                              color: theme.fg, fontSize: 12,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              const isLesson = m.kind === "lesson" && m.sender === "advisor";
              return (
              <div key={i} style={{ display: "flex", margin: "10px 0", justifyContent: m.sender === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth: isLesson ? "94%" : "82%",
                  padding: isLesson ? "18px 22px" : "14px 18px",
                  borderRadius: 16, lineHeight: 1.6, fontSize: 14.5,
                  background: m.sender === "user"
                    ? `linear-gradient(135deg, ${accent}, ${accent}aa)`
                    : (isLesson
                        ? `linear-gradient(180deg, ${accent}10, rgba(0,0,0,0.35))`
                        : "rgba(255,255,255,0.04)"),
                  color: m.sender === "user" ? "#0a0612" : theme.fg,
                  border: m.sender === "user"
                    ? "none"
                    : (isLesson ? `1.5px solid ${accent}66` : `1px solid ${accent}24`),
                  boxShadow: isLesson ? `0 0 24px ${accent}22` : "none",
                  borderBottomRightRadius: m.sender === "user" ? 4 : 16,
                  borderBottomLeftRadius: m.sender === "user" ? 16 : 4,
                }}>
                  {isLesson && (
                    <div className="flex items-center gap-2 mb-3 pb-2" style={{ borderBottom: `1px dashed ${accent}44` }}>
                      <BookOpen size={14} style={{ color: accent }} />
                      <span className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>
                        {lang === "fr" ? "Leçon · ~3 min" : "Lesson · ~3 min"}
                      </span>
                    </div>
                  )}
                  {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                  {m.sender === "advisor" && !isLesson && (
                    <div className="mt-2">
                      <VoiceButton text={m.message} lang={lang} accent={accent} testId={`${persona}-voice-${i}`} />
                    </div>
                  )}
                  {isLesson && voiceSupported && (
                    <button
                      type="button"
                      onClick={() => toggleLessonTTS(i, m.message)}
                      data-testid={`${persona}-lesson-listen-${i}`}
                      className="mt-4 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full transition-all"
                      style={{
                        background: lessonPlayingIdx === i
                          ? `linear-gradient(135deg, ${accent}, ${accent}aa)`
                          : `${accent}22`,
                        border: `1.5px solid ${accent}aa`,
                        color: lessonPlayingIdx === i ? "#0a0612" : accent,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {lessonPlayingIdx === i
                        ? <><Loader2 size={13} className="animate-spin" /> {lang === "fr" ? "Arrêter l'écoute" : "Stop"}</>
                        : <><Headphones size={13} /> {lang === "fr" ? "🎧 Écouter la leçon" : "🎧 Listen to lesson"}</>}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
            {(loading || lessonLoading) && (
              <div style={{ display: "flex", margin: "10px 0" }}>
                <div style={{ padding: "12px 16px", borderRadius: 16, background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}30`, color: theme.fg, display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-sm">
                    {lessonLoading
                      ? (lang === "fr" ? "Le Maître prépare la leçon…" : "The Teacher is composing the lesson…")
                      : (lang === "fr" ? "Le silence prépare la parole…" : "Silence shapes the word…")}
                  </span>
                </div>
              </div>
            )}
          </div>

          <form style={{ display: "flex", gap: 10, alignItems: "flex-end", borderTop: `1px solid ${accent}20`, paddingTop: 14 }}
                onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea data-testid={`${persona}-input`}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={inputPlaceholder?.[lang] || (lang === "fr" ? "Pose ta question au Maître…" : "Ask the Teacher…")}
                      rows={2}
                      style={{ flex: 1, resize: "none", padding: "12px 14px", borderRadius: 14,
                               background: "rgba(0,0,0,0.3)", border: `1px solid ${accent}30`,
                               color: theme.fg, fontSize: 14.5, outline: "none" }}
                      disabled={loading} />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={wipe} data-testid={`${persona}-clear`}
                      style={{ padding: "12px 14px", borderRadius: 12,
                               background: "rgba(180,40,40,0.18)", border: "1px solid rgba(255,120,120,0.3)",
                               color: "#ffb8a8" }}>
                <Trash2 size={14} />
              </button>
              <button type="submit" disabled={loading || !input.trim()} data-testid={`${persona}-send`}
                      style={{ padding: "12px 18px", borderRadius: 12, display: "inline-flex", gap: 8, alignItems: "center",
                               background: `linear-gradient(135deg, ${accent}, ${accent}bb)`,
                               color: "#0a0612", fontWeight: 600, fontSize: 13,
                               fontFamily: "'IBM Plex Mono', monospace",
                               opacity: (loading || !input.trim()) ? 0.4 : 1 }}>
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {lang === "fr" ? "Envoyer" : "Send"}
              </button>
            </div>
          </form>
          <ChatDisclaimer testId={`${persona}-ai-disclaimer`} color={`${accent}aa`} />
        </div>
      </section>

    </div>
  );
}
